import { injectIntl } from "react-intl";
import { useCallback, useEffect, useState } from "react";
import dayjs from "dayjs";
import apiService from "src/services/api.service";
import { ListType } from "src/helpers/Enums";
import { CHART_COLORS } from "./constants";
import AnalyticsHeader from "./AnalyticsHeader";
import MetricCards from "./MetricCards";
import RevenueCostTrends from "./RevenueCostTrends";
import RevenueByList from "./RevenueByList";
import RevenueBySegment from "./RevenueBySegment";
import ListPerformance from "./ListPerformance";
import EngagementMetricCards from "./EngagementMetricCards";
import ClickConversionTrends from "./ClickConversionTrends";
import ClickThroughRateTrend from "./ClickThroughRateTrend";
import ClicksByList from "./ClicksByList";
import ClicksBySegment from "./ClicksBySegment";
import TopCampaignsByClicks from "./TopCampaignsByClicks";

const buildAnalyticsFilters = (
  activeRange,
  dateRange,
  selectedList,
  selectedSegments
) => {
  const listIds = selectedList?._id === "all" ? [] : [selectedList._id];
  const segments = selectedSegments?.includes("all") ? [] : selectedSegments;

  const hasCustomDate = dateRange?.[0] && dateRange?.[1];

  return {
    range: hasCustomDate ? "Custom" : activeRange,
    ...(hasCustomDate && {
      startDate: dateRange[0].format("YYYY-MM-DD"),
      endDate: dateRange[1].format("YYYY-MM-DD"),
    }),
    listIds,
    segments,
  };
};

const buildTrendFilters = (selectedList, selectedSegments) => {
  const listIds = selectedList?._id === "all" ? [] : [selectedList._id];
  const segments = selectedSegments?.includes("all") ? [] : selectedSegments;

  return { listIds, segments };
};

const isSuccess = (response) => response?.status?.toString().startsWith("2");

const SMSAnalytics = () => {
  const [activeTab, setActiveTab] = useState("Overview");
  const [activeRange, setActiveRange] = useState("30D");
  const [dateRange, setDateRange] = useState(null);
  const [smsLists, setSmsLists] = useState([]);
  const [segmentOptions, setSegmentOptions] = useState([]);
  const [selectedList, setSelectedList] = useState({
    _id: "all",
    name: "All Lists",
  });
  const [selectedSegments, setSelectedSegments] = useState(["all"]);

  const [overviewMetrics, setOverviewMetrics] = useState(null);
  const [revenueCostTrends, setRevenueCostTrends] = useState([]);
  const [revenueByList, setRevenueByList] = useState([]);
  const [revenueByListTotal, setRevenueByListTotal] = useState(0);
  const [revenueBySegment, setRevenueBySegment] = useState([]);
  const [listPerformance, setListPerformance] = useState([]);

  const [engagementMetrics, setEngagementMetrics] = useState(null);
  const [clickConversionTrends, setClickConversionTrends] = useState([]);
  const [ctrTrends, setCtrTrends] = useState([]);
  const [clicksByList, setClicksByList] = useState([]);
  const [clicksBySegment, setClicksBySegment] = useState([]);
  const [topCampaigns, setTopCampaigns] = useState([]);

  useEffect(() => {
    apiService.getAllLists().then((response) => {
      const lists =
        response.data?.filter((item) => item.type === ListType.SMS) || [];
      setSmsLists(lists);
    });

    apiService.getSmsAnalyticsSegmentList().then((response) => {
      if (isSuccess(response)) {
        setSegmentOptions(response.data?.segments || []);
      }
    });
  }, []);

  const fetchOverviewData = useCallback(async () => {
    const filters = buildAnalyticsFilters(
      activeRange,
      dateRange,
      selectedList,
      selectedSegments
    );
    const trendFilters = buildTrendFilters(selectedList, selectedSegments);

    const [
      overviewRes,
      revenueCostRes,
      revenueByListRes,
      revenueBySegmentRes,
      listPerformanceRes,
    ] = await Promise.all([
      apiService.getSmsAnalyticsOverviewHeader(filters),
      apiService.getSmsAnalyticsRevenueCostTrend(trendFilters),
      apiService.getSmsAnalyticsRevenueByList(filters),
      apiService.getSmsAnalyticsRevenueBySegment(filters),
      apiService.getSmsAnalyticsListPerformance(filters),
    ]);

    if (isSuccess(overviewRes)) {
      setOverviewMetrics(overviewRes.data);
    }

    if (isSuccess(revenueCostRes)) {
      setRevenueCostTrends(
        (revenueCostRes.data?.data || []).map((item) => ({
          month: item.label,
          revenue: item.totalRevenue,
          cost: item.totalCost,
        }))
      );
    }

    if (isSuccess(revenueByListRes)) {
      setRevenueByListTotal(revenueByListRes.data?.totalRevenue || 0);
      setRevenueByList(
        (revenueByListRes.data?.data || []).map((item, index) => ({
          name: item.listName,
          value: item.totalRevenue,
          percent: item.percentage,
          color: CHART_COLORS[index % CHART_COLORS.length],
        }))
      );
    }

    if (isSuccess(revenueBySegmentRes)) {
      setRevenueBySegment(
        (revenueBySegmentRes.data?.data || []).map((item) => ({
          name: item.segmentName,
          revenue: item.totalRevenue,
        }))
      );
    }

    if (isSuccess(listPerformanceRes)) {
      setListPerformance(
        (listPerformanceRes.data?.data || []).map((item) => ({
          name: item.list_name,
          revenue: item.totalRevenue,
          cost: item.totalCost,
          profit: item.totalProfit,
          roi: item.roi,
          messages: item.totalMessages,
          campaigns: item.totalCalendar,
        }))
      );
    }
  }, [activeRange, dateRange, selectedList, selectedSegments]);

  const fetchEngagementData = useCallback(async () => {
    const filters = buildAnalyticsFilters(
      activeRange,
      dateRange,
      selectedList,
      selectedSegments
    );
    const trendFilters = buildTrendFilters(selectedList, selectedSegments);

    const [
      engagementRes,
      clickConversionRes,
      ctrRes,
      clicksByListRes,
      clicksBySegmentRes,
      topCampaignsRes,
    ] = await Promise.all([
      apiService.getSmsAnalyticsEngagementHeader(filters),
      apiService.getSmsAnalyticsClickConversionTrend(trendFilters),
      apiService.getSmsAnalyticsCtrTrend(trendFilters),
      apiService.getSmsAnalyticsClicksByList(filters),
      apiService.getSmsAnalyticsClicksBySegment(filters),
      apiService.getSmsAnalyticsTopCalendarByClick(filters),
    ]);

    if (isSuccess(engagementRes)) {
      setEngagementMetrics(engagementRes.data);
    }

    if (isSuccess(clickConversionRes)) {
      setClickConversionTrends(
        (clickConversionRes.data?.data || []).map((item) => ({
          month: item.label,
          clicks: item.totalClicks,
          conversions: item.totalConversions,
        }))
      );
    }

    if (isSuccess(ctrRes)) {
      setCtrTrends(
        (ctrRes.data?.data || []).map((item) => ({
          month: item.label,
          ctr: item.CTR,
        }))
      );
    }

    if (isSuccess(clicksByListRes)) {
      setClicksByList(
        (clicksByListRes.data?.data || []).map((item) => ({
          name: item.listName,
          clicks: item.totalClicks,
        }))
      );
    }

    if (isSuccess(clicksBySegmentRes)) {
      setClicksBySegment(
        (clicksBySegmentRes.data?.data || []).map((item) => ({
          name: item.segmentName,
          clicks: item.totalClicks,
        }))
      );
    }

    if (isSuccess(topCampaignsRes)) {
      setTopCampaigns(
        (topCampaignsRes.data?.data || []).map((item) => ({
          date: dayjs(item.date).format("MMM D, YYYY"),
          list: item.list_name,
          offer: item.offer_name,
          segment: item.segment_name,
          messages: item.segmentSize,
          clicks: item.click,
          conversions: item.conversion,
          ctr: item.CTR,
          convRate: item.CR,
        }))
      );
    }
  }, [activeRange, dateRange, selectedList, selectedSegments]);

  useEffect(() => {
    fetchOverviewData();
    fetchEngagementData();
  }, [fetchOverviewData, fetchEngagementData]);

  const handleRangeChange = (range) => {
    setActiveRange(range);
    setDateRange(null);
  };

  const handleDateRangeChange = (range) => {
    setDateRange(range);
    setActiveRange(null);
  };

  const handleSegmentToggle = (segmentName) => {
    if (segmentName === "all") {
      setSelectedSegments(["all"]);
      return;
    }

    setSelectedSegments((prev) => {
      const withoutAll = prev.filter((name) => name !== "all");
      if (withoutAll.includes(segmentName)) {
        const next = withoutAll.filter((name) => name !== segmentName);
        return next.length === 0 ? ["all"] : next;
      }
      return [...withoutAll, segmentName];
    });
  };

  return (
    <div className="Page SmsAnalyticsPage">
      <AnalyticsHeader
        activeTab={activeTab}
        onTabChange={setActiveTab}
        activeRange={activeRange}
        onRangeChange={handleRangeChange}
        lists={smsLists}
        selectedList={selectedList}
        onListChange={setSelectedList}
        segmentOptions={segmentOptions}
        selectedSegments={selectedSegments}
        onSegmentToggle={handleSegmentToggle}
        dateRange={dateRange}
        onDateRangeChange={handleDateRangeChange}
      />

      {activeTab === "Overview" ? (
        <div className="SmsAnalyticsContent">
          <MetricCards metrics={overviewMetrics} />
          <div className="SmsAnalyticsChartsRow">
            <RevenueCostTrends data={revenueCostTrends} />
            <RevenueByList
              data={revenueByList}
              totalRevenue={revenueByListTotal}
            />
          </div>
          <RevenueBySegment data={revenueBySegment} />
          <ListPerformance data={listPerformance} />
        </div>
      ) : (
        <div className="SmsAnalyticsContent">
          <EngagementMetricCards metrics={engagementMetrics} />
          <div className="SmsAnalyticsChartsRow">
            <ClickConversionTrends data={clickConversionTrends} />
            <ClickThroughRateTrend data={ctrTrends} />
          </div>
          <div className="SmsAnalyticsChartsRow">
            <ClicksByList data={clicksByList} />
            <ClicksBySegment data={clicksBySegment} />
          </div>
          <TopCampaignsByClicks data={topCampaigns} />
        </div>
      )}
    </div>
  );
};

export default injectIntl(SMSAnalytics);
